import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import './MyOrganizations.css';

function MyOrganizations() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organizations, setOrganizations] = useState([]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!auth.isAuthenticated || !auth.user?.id_token) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const idToken = auth.user.id_token;
        const orgsData = await apiService.getUserOrganizations(idToken);
        setOrganizations(orgsData || []);
      } catch (err) {
        console.error('Error fetching organizations:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [auth.isAuthenticated, auth.user]);

  if (loading) {
    return (
      <div className="my-orgs-container">
        <div className="loading-container">
          <p>Loading organizations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-orgs-container">
        <div className="error-container">
          <h2>Error Loading Organizations</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <div className="my-orgs-container">
        <div className="empty-state">
          <h2>No Organizations</h2>
          <p>You are not a member of any organizations yet.</p>
        </div>
      </div>
    );
  }

  // Separate organizations into admin and member groups
  const adminOrganizations = organizations.filter(org => org.is_admin === 1 || org.is_admin === true);
  const memberOrganizations = organizations.filter(org => org.is_admin === 0 || org.is_admin === false);

  return (
    <div className="my-orgs-container">
      <div className="page-header">
        <h1>My Organizations</h1>
      </div>

      {/* Admin Organizations */}
      {adminOrganizations.length > 0 && (
        <section className="org-section">
          <div className="section-header">
            <h2>Organizations I Manage</h2>
            <span className="org-count">{adminOrganizations.length} organization{adminOrganizations.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="org-grid">
            {adminOrganizations.map(org => (
              <div
                key={org.organization_id}
                className="org-card admin"
                onClick={() => navigate(`/organization/${org.organization_slug || org.organization_id}`)}
              >
                <div className="org-card-avatar">
                  {org.organization_name?.charAt(0)?.toUpperCase() || 'O'}
                </div>
                <div className="org-card-info">
                  <h3 className="org-card-name">{org.organization_name}</h3>
                  <p className="org-card-email">{org.organization_email}</p>
                  <div className="org-card-badges">
                    <span className="org-badge">Organization</span>
                    <span className="admin-badge">Admin</span>
                  </div>
                  <p className="org-card-joined">Joined {new Date(org.date_joined).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Member Organizations */}
      {memberOrganizations.length > 0 && (
        <section className="org-section">
          <div className="section-header">
            <h2>Organizations I'm Part Of</h2>
            <span className="org-count">{memberOrganizations.length} organization{memberOrganizations.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="org-grid">
            {memberOrganizations.map(org => (
              <div
                key={org.organization_id}
                className="org-card member"
                onClick={() => navigate(`/organization/${org.organization_slug || org.organization_id}`)}
              >
                <div className="org-card-avatar">
                  {org.organization_name?.charAt(0)?.toUpperCase() || 'O'}
                </div>
                <div className="org-card-info">
                  <h3 className="org-card-name">{org.organization_name}</h3>
                  <p className="org-card-email">{org.organization_email}</p>
                  <div className="org-card-badges">
                    <span className="org-badge">Organization</span>
                  </div>
                  <p className="org-card-joined">Joined {new Date(org.date_joined).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Empty State */}
      {organizations.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">🏢</div>
          <h2>No Organizations Yet</h2>
          <p>You are not a member of any organizations. Wait for an invitation or contact an administrator to join.</p>
        </div>
      )}
    </div>
  );
}

export default MyOrganizations;
